import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Component,
  EventEmitter,
  Output,
  OnInit,
  inject,
  DestroyRef
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CommonModule } from '@angular/common';

import { ButtonModule } from 'primeng/button';
import { PopoverModule } from 'primeng/popover';
import { AvatarModule } from 'primeng/avatar';
import { DialogService } from 'primeng/dynamicdialog';

import { AuthGuard } from '../../../core/guards/auth.guard';
import { AuthUserService } from '../../../core/services/auth-user.service';
import { ResponsiveService } from '../../../core/services/responsive.service';
import { SharedModuleService } from '../../../core/services/shared-module.service';
import { SideBarToggleService } from '../../../core/services/side-bar-toggle.service';
import { LoggerService } from '../../../core/services/logger.service';
import { ToastrShowService } from '../../../core/services/toastr.service';
import { WalkthroughService } from '../../../core/services/walkthrough.service';
import { USERROLES } from '../../../core/common-enums/enum';
import { BrokerBsePasswordUpdateComponent } from '../../../shared/broker-bse-password-update/broker-bse-password-update.component';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  standalone: true,
  providers: [DialogService],
  imports: [CommonModule, ButtonModule, PopoverModule, AvatarModule]
})
export class NavbarComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  mainHeadingValue?: string;
  @Output() sidenav = new EventEmitter<void>();
  mobile: boolean = false;
  isLoggedIn = true;
  isSideBarOpened = true;
  userFirstName: string = '';
  userLastName: string = '';
  userFirstNameAlphabet: string = '';
  userLastNameAlphabet: string = '';
  userId: string = '';
  role: string = '';
  brokerAdminfirstTimeLogin?: boolean;
  isExpanded!: boolean;
  hasBSECreds: boolean = false;
  userOrgArn: string = '';
  helpAvailable: boolean = false;

  // Use inject() for dependencies
  private _authService = inject(AuthUserService);
  private router = inject(Router);
  private _sideBarToggleService = inject(SideBarToggleService);
  private _responsiveService = inject(ResponsiveService);
  private sharedModuleService = inject(SharedModuleService);
  private _authGuard = inject(AuthGuard);
  private dialogService = inject(DialogService);
  private logger = inject(LoggerService);
  private toastr = inject(ToastrShowService);
  private walkthroughService = inject(WalkthroughService);

  ngOnInit(): void {
    this._authService.isLoggedInObservable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: boolean) => {
        this.isLoggedIn = res;
      });
    this._sideBarToggleService.isSideOpened
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: boolean) => {
        this.isSideBarOpened = res;
      });
    this._authService
      .getUserDetailBehaviorSubject()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (data) {
          this.userId = data.userId;
          this.role = data.newRoleFinal.roleDescription;
          if (
            this.role === USERROLES.BROKER_ADMIN ||
            this.role === USERROLES.ADVISOR
          ) {
            this.brokerAdminfirstTimeLogin = data.brokerAdminfirstTimeLogin;
          }
          this.hasBSECreds = data?.hasBSECreds;
          this.userFirstName = data.firstName;
          this.userLastName = data.lastName;
          this.userOrgArn = !(this.role === USERROLES.CLIENT)
            ? data.orgArn
            : '';
          this.userFirstNameAlphabet = this.userFirstName?.charAt(0) || '';
          this.userLastNameAlphabet = this.userLastName?.charAt(0) || '';
        }
      });
    this.onResize();
    this._responsiveService.checkWidth();
    this.sharedModuleService
      .getNavbarHeading()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (data) {
          this.mainHeadingValue = data.mainHeading;
        }
        if (
          this.role === USERROLES.BROKER_ADMIN ||
          this.role === USERROLES.RELATIONSHIP_MANAGER
        ) {
          this.mainHeadingValue = 'Clients';
        }
        // Dynamic heading for ADMIN based on route
        if (this.role === USERROLES.ADMIN) {
          const currentUrl = this.router.url;
          if (currentUrl.includes('/dbf-file-viewer')) {
            this.mainHeadingValue = 'DBF File Viewer';
          } else {
            this.mainHeadingValue = 'Brokers/Advisors';
          }
        }
      });

    // Update heading on route changes for ADMIN
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationEnd && this.role === USERROLES.ADMIN) {
          const currentUrl = event.url;
          if (currentUrl.includes('/dbf-file-viewer')) {
            this.mainHeadingValue = 'DBF File Viewer';
          } else {
            this.mainHeadingValue = 'Brokers/Advisors';
          }
        }
      });

    this._sideBarToggleService
      .getIsExpandedUpdate()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: { text: boolean }) => {
        this.isExpanded = res.text;
      });

    // Subscribe to whether Help should be shown in the navbar
    this.sharedModuleService
      .getNavbarHelpAvailable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v: boolean) => (this.helpAvailable = !!v));
  }

  logout(): void {
    this.sharedModuleService.hasCheckedCreds = false;
    this._authGuard.logout();
  }

  toggle(): void {
    this.sidenav.emit();
  }

  toggleSidebar(): void {
    this.isExpanded = !this.isExpanded;
    this._sideBarToggleService.sendIsExpandedUpdate(this.isExpanded);
  }

  showProfile(): void {
    // Store own profile name in localStorage for breadcrumb
    const fullName =
      `${this.userFirstName || ''} ${this.userLastName || ''}`.trim();
    if (fullName) {
      localStorage.setItem('profileName', fullName);
    }

    if (this.role === USERROLES.RELATIONSHIP_MANAGER) {
      this.router.navigate(['relationshipManager/profile', this.userId]);
    }
    if (this.role === USERROLES.BROKER_ADMIN) {
      this.router.navigate(['broker/profile', this.userId]);
    }
    if (this.role === USERROLES.ADVISOR) {
      this.router.navigate(['advisor/profile', this.userId]);
    }
    if (this.role === USERROLES.CLIENT) {
      this.router.navigate(['client/profile']);
      this.sharedModuleService.sideNavIndex.set(4);
    }
  }

  onResize(): void {
    this._responsiveService
      .getMobileStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isMobile: boolean) => {
        this.mobile = isMobile;
      });
  }

  startHelp(): void {
    this.walkthroughService.triggerRestart();
  }

  onbaordBse(): void {
    this.logger.userAction(
      'Opening BSE password setup modal',
      { userId: this.userId },
      'NavbarComponent.onbaordBse'
    );

    const dialogRef = this.dialogService.open(
      BrokerBsePasswordUpdateComponent,
      {
        header: '',
        width: 'auto',
        height: 'auto',
        closable: false,
        modal: true,
        focusOnShow: false,
        draggable: false,
        resizable: false,
        showHeader: false,
        data: {
          modalData: { brokerAdminUserId: this.userId }
        },
        contentStyle: {
          padding: '0',
          border: 'none',
          'border-radius': '0',
          background: 'transparent',
          'box-shadow': 'none'
        },
        styleClass: 'bse-password-modal transparent-dialog'
      }
    );
  }
}
